import { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RTooltip } from "recharts";
import {
  FileProtectOutlined, PlusCircleOutlined, SafetyCertificateOutlined, EyeOutlined,
} from "@ant-design/icons";
import { fetchTmBreakdown } from "../../../services/customer360Service";
import {
  cardStyle, num, dateTime, chip, TONE, titleCase,
  StatTile, TileGrid, PanelHeader, PanelTable, PanelSkeleton, PanelEmpty,
  PanelError, usePanelData,
} from "./shared";

/**
 * Where this account's trademarks came from, and who may touch them.
 *
 * Two questions on one tab because they answer each other: the split between
 * subscribed and manually added marks says what the account holds, and the
 * delegation list says which sub-user can act on which slice of it.
 */

const SCOPE_TONE = {
  tm: TONE.blue,
  attorney_number: TONE.violet,
  client: TONE.green,
};

const TmBreakdownTab = ({ userId, active }) => {
  const p = usePanelData(fetchTmBreakdown, userId, active);

  const d = p.data || {};
  const subscribed = Number(d.total_tm_subscribed) || 0;
  const manual = Number(d.total_tm_manual) || 0;
  const monitoring = Number(d.total_tm_monitoring) || 0;

  /* Slices with nothing in them are dropped: a legend entry against a zero-width
     arc is a label pointing at nothing. */
  const donut = useMemo(() => ([
    { name: "Subscribed", value: subscribed, fill: "#6366f1" },
    { name: "Manually added", value: manual, fill: "#14b8a6" },
    { name: "In monitoring", value: monitoring, fill: "#f59e0b" },
  ].filter((s) => s.value > 0)), [subscribed, manual, monitoring]);

  /* Grouped by person, because the question being asked is "what can this user
     do", not "list every scope row". */
  const scopesByUser = useMemo(() => {
    const groups = new Map();
    (d.member_scopes || []).forEach((s) => {
      const key = s.user_name || "Unknown user";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(s);
    });
    return Array.from(groups.entries());
  }, [d.member_scopes]);

  if (p.loading && !p.data) return <PanelSkeleton tiles={2} />;
  if (p.error) return <PanelError error={p.error} onRetry={p.reload} />;

  const manualColumns = [
    {
      title: "TM number", dataIndex: "tm_number", width: 150,
      render: (v) => <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 12.5 }}>{v || "—"}</span>,
    },
    {
      title: "Added by", dataIndex: "added_by_name", width: 200,
      render: (v) => <span style={{ fontSize: 12.5, color: "#334155", fontWeight: 500 }}>{v || "—"}</span>,
    },
    {
      title: "Added on", dataIndex: "created_at", width: 180,
      render: (v) => <span style={{ fontSize: 12.5, color: "#94a3b8", whiteSpace: "nowrap" }}>{dateTime(v)}</span>,
    },
  ];

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px,2fr) minmax(240px,1fr)", gap: 20, marginBottom: 20, alignItems: "stretch" }}>
        <TileGrid min={190}>
          <StatTile
            icon={<FileProtectOutlined />} label="Subscribed trademarks" value={num(subscribed)}
            color="#6366f1" bg="#eef2ff" sub="Covered by the active plan"
          />
          <StatTile
            icon={<PlusCircleOutlined />} label="Manually added" value={num(manual)}
            color="#0d9488" bg="#f0fdfa" sub="Added by the customer's own team"
          />
          <StatTile
            icon={<EyeOutlined />} label="In monitoring" value={num(monitoring)}
            color="#d97706" bg="#fffbeb" sub="Actively watched right now"
          />
        </TileGrid>

        <div style={{ ...cardStyle, padding: "16px 16px 8px", display: "flex", flexDirection: "column" }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            Portfolio split
          </div>
          {donut.length === 0 ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#cbd5e1", fontSize: 12.5 }}>
              No trademarks yet
            </div>
          ) : (
            <div style={{ flex: 1, minHeight: 150 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donut} dataKey="value" nameKey="name"
                    innerRadius="58%" outerRadius="86%" paddingAngle={2} stroke="none"
                  >
                    {donut.map((s) => <Cell key={s.name} fill={s.fill} />)}
                  </Pie>
                  <RTooltip
                    formatter={(v, n) => [num(v), n]}
                    contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          <div style={{ display: "flex", gap: 14, justifyContent: "center", padding: "8px 0 4px", flexWrap: "wrap" }}>
            {donut.map((s) => (
              <span key={s.name} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#64748b" }}>
                <span style={{ width: 9, height: 9, borderRadius: 3, background: s.fill }} />
                {s.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <PanelHeader
          icon={<PlusCircleOutlined />}
          title="Manually added trademarks"
          subtitle="Marks the customer's team added outside the subscription"
          iconBg="linear-gradient(135deg,#0d9488,#14b8a6)"
        />
        <PanelTable
          columns={manualColumns}
          rows={p.rows}
          loading={p.loading}
          page={p.page}
          total={p.total}
          perPage={p.perPage}
          onPage={p.goToPage}
          empty="No trademarks have been added manually on this account."
          scrollX={560}
        />
      </div>

      <div style={cardStyle}>
        <PanelHeader
          icon={<SafetyCertificateOutlined />}
          title="Data-scope delegation"
          subtitle="Which sub-user can manage what"
          iconBg="linear-gradient(135deg,#7c3aed,#8b5cf6)"
          right={chip(`${num(scopesByUser.length)} users`, TONE.violet)}
        />
        {scopesByUser.length === 0 ? (
          <PanelEmpty message="No scopes delegated — every member sees the whole account." />
        ) : (
          <div style={{ padding: "6px 0" }}>
            {scopesByUser.map(([user, scopes]) => (
              <div key={user} style={{ padding: "14px 22px", borderBottom: "1px solid #f8fafc" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>{user}</span>
                  {chip(`${scopes.length} scope${scopes.length > 1 ? "s" : ""}`, TONE.slate)}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {scopes.map((s, i) => (
                    <div
                      key={`${s.scope_value}-${i}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 8, padding: "7px 12px",
                        borderRadius: 10, border: "1px solid #e2e8f0", background: "#fafbff",
                      }}
                    >
                      {chip(titleCase(s.scope_level), SCOPE_TONE[s.scope_level] || TONE.slate)}
                      <span style={{ fontSize: 12.5, color: "#0f172a", fontWeight: 600 }}>
                        {s.scope_value || "—"}
                      </span>
                      {s.module && (
                        <span style={{ fontSize: 11.5, color: "#94a3b8" }}>in {titleCase(s.module)}</span>
                      )}
                      {/* Only when it adds something the scope value does not
                          already say — an attorney-number scope repeating its own
                          number is noise. */}
                      {s.attorney_number && s.attorney_number !== s.scope_value && (
                        <span style={{ fontSize: 11.5, color: "#94a3b8" }}>· attorney {s.attorney_number}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default TmBreakdownTab;
